/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ПОЛНЫЙ ПУТЬ: D:\QR\QR-WEB\dashboard\src\services\api.js
 * 
 * ВЕРСИЯ: 3.0.0 (ПОЛНАЯ ОБЪЕДИНЁННАЯ)
 * ДАТА: 15 ноября 2025
 * СТАТУС: ✅ ГОТОВО
 * 
 * СОДЕРЖИТ:
 * - authAPI (логин, регистрация, выход)
 * - profileAPI (профиль пользователя)
 * - balanceAPI (баланс)
 * - transactionsAPI (транзакции)
 * - qrAPI (QR коды)
 * - adminAPI (администраторские операции)
 *   ├── users (5 методов)
 *   ├── qr (3 метода)
 *   ├── transactions (1 метод)
 *   ├── statistics (1 метод)
 *   ├── businesses (3 метода)
 *   ├── promo (5 методов)
 *   ├── security (5 NEW методов)
 *   ├── tron (8 NEW методов)
 *   └── stats (5 методов)
 * ═══════════════════════════════════════════════════════════════════════════
 */

const API_URL = 'https://qr-money.onrender.com/api';

const getToken = () => localStorage.getItem('authToken');
const getUserId = () => localStorage.getItem('userId');

export const apiCall = async (endpoint, options = {}) => {
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const token = getToken();
  if (token) {
    defaultOptions.headers['Authorization'] = `Bearer ${token}`;
  }

  const finalOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...(options.headers || {}),
    },
  };

  const url = `${API_URL}${endpoint}`;

  console.log(`🌐 API Request: ${finalOptions.method || 'GET'} ${url}`);

  const response = await fetch(url, finalOptions);

  console.log(`📥 API Response: ${response.status}`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || `API Error: ${response.status}`);
  }

  return response.json();
};

// ═══════════════════════════════════════════════════════════════════════════
// AUTH API
// ═══════════════════════════════════════════════════════════════════════════

export const authAPI = {
  register: async (email, password, phone) => {
    const data = await apiCall('/auth/register', {
      method: 'POST',
      body: JSON.stringify({
        email: email.trim(),
        password,
        phone: phone.trim(),
      }),
    });
    return data;
  },

  login: async (email, password) => {
    const data = await apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        email: email.trim(),
        password,
      }),
    });
    return data;
  },

  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userPhone');
    localStorage.removeItem('userTier');
    localStorage.removeItem('userStatus');
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// PROFILE API
// ═══════════════════════════════════════════════════════════════════════════

export const profileAPI = {
  getProfile: async (userId = null) => {
    const id = userId || getUserId() || 1;
    const data = await apiCall(`/profile/${id}`, {
      method: 'GET',
    });
    return data;
  },

  updateProfile: async (userId, profileData) => {
    const id = userId || getUserId() || 1;
    const data = await apiCall(`/profile/${id}/update`, {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
    return data;
  },

  getStats: async (userId = null) => {
    const id = userId || getUserId() || 1;
    const data = await apiCall(`/profile/${id}/stats`, {
      method: 'GET',
    });
    return data;
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// BALANCE API
// ═══════════════════════════════════════════════════════════════════════════

export const balanceAPI = {
  getBalance: async (userId = null) => {
    const id = userId || getUserId() || 1;
    const data = await apiCall(`/balance/${id}`, {
      method: 'GET',
    });
    return data;
  },

  addBalance: async (userId, amount, currency = 'DAR') => {
    const id = userId || getUserId() || 1;
    const data = await apiCall(`/balance/${id}/add`, {
      method: 'POST',
      body: JSON.stringify({
        amount,
        currency,
      }),
    });
    return data;
  },

  getHistory: async (userId = null) => {
    const id = userId || getUserId() || 1;
    const data = await apiCall(`/balance/${id}/history`, {
      method: 'GET',
    });
    return data;
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// TRANSACTIONS API
// ═══════════════════════════════════════════════════════════════════════════

export const transactionsAPI = {
  create: async (fromUserId, toUserId, amount, currency = 'DAR', qrCodeId = null) => {
    const data = await apiCall('/transactions/create', {
      method: 'POST',
      body: JSON.stringify({
        from_user_id: fromUserId,
        to_user_id: toUserId,
        amount,
        currency,
        qr_code_id: qrCodeId,
      }),
    });
    return data;
  },

  getHistory: async (userId = null, type = 'all', page = 1, limit = 20) => {
    const id = userId || getUserId() || 1;
    const params = new URLSearchParams({
      type,
      page: page.toString(),
      limit: limit.toString(),
    });
    const data = await apiCall(`/profile/${id}/transactions?${params}`, {
      method: 'GET',
    });
    return data;
  },

  getStats: async (userId = null) => {
    const id = userId || getUserId() || 1;
    const data = await apiCall(`/profile/${id}/stats`, {
      method: 'GET',
    });
    return data;
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// QR API
// ═══════════════════════════════════════════════════════════════════════════

export const qrAPI = {
  create: async (toUserId, amount, currency = 'DAR', expiresIn = 3600) => {
    const fromUserId = getUserId() || 1;
    const data = await apiCall('/qr/create', {
      method: 'POST',
      body: JSON.stringify({
        from_user_id: fromUserId,
        to_user_id: toUserId,
        amount,
        currency,
        expires_in: expiresIn,
      }),
    });
    return data;
  },

  getQR: async (qrCodeId) => {
    const data = await apiCall(`/qr/${qrCodeId}`, {
      method: 'GET',
    });
    return data;
  },

  scan: async (qrCodeId) => {
    const toUserId = getUserId() || 1;
    const data = await apiCall('/qr/scan', {
      method: 'POST',
      body: JSON.stringify({
        qr_code_id: qrCodeId,
        to_user_id: toUserId,
      }),
    });
    return data;
  },

  getHistory: async (userId = null, status = 'all') => {
    const id = userId || getUserId() || 1;
    const params = new URLSearchParams({ status });
    const data = await apiCall(`/qr/user/${id}?${params}`, {
      method: 'GET',
    });
    return data;
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// ADMIN API
// ═══════════════════════════════════════════════════════════════════════════

export const adminAPI = {
  users: {
    getAllUsers: async () => {
      try {
        console.log('📝 [ADMIN-API] Загружаю всех пользователей...');
        const data = await apiCall('/admin/users', { method: 'GET' });
        console.log('✅ [ADMIN-API] Пользователи получены:', data);
        return data;
      } catch (error) {
        console.error('❌ [ADMIN-API] Ошибка получения пользователей:', error);
        throw error;
      }
    },

    getUserById: async (userId) => {
      try {
        console.log(`📝 [ADMIN-API] Получаю пользователя ${userId}...`);
        const data = await apiCall(`/admin/users/${userId}`, { method: 'GET' });
        console.log(`✅ [ADMIN-API] Пользователь ${userId} получен:`, data);
        return data;
      } catch (error) {
        console.error(`❌ [ADMIN-API] Ошибка получения пользователя ${userId}:`, error);
        throw error;
      }
    },

    blockUser: async (userId) => {
      try {
        console.log(`🚫 [ADMIN-API] Блокирую пользователя ${userId}...`);
        const data = await apiCall(`/admin/users/${userId}/block`, {
          method: 'POST',
          body: JSON.stringify({}),
        });
        console.log(`✅ [ADMIN-API] Пользователь ${userId} заблокирован:`, data);
        return data;
      } catch (error) {
        console.error(`❌ [ADMIN-API] Ошибка блокировки пользователя ${userId}:`, error);
        throw error;
      }
    },

    unblockUser: async (userId) => {
      try {
        console.log(`🔓 [ADMIN-API] Разблокирую пользователя ${userId}...`);
        const data = await apiCall(`/admin/users/${userId}/unblock`, {
          method: 'POST',
          body: JSON.stringify({}),
        });
        console.log(`✅ [ADMIN-API] Пользователь ${userId} разблокирован:`, data);
        return data;
      } catch (error) {
        console.error(`❌ [ADMIN-API] Ошибка разблокировки пользователя ${userId}:`, error);
        throw error;
      }
    },

    deleteUser: async (userId) => {
      try {
        console.log(`🗑️ [ADMIN-API] Удаляю пользователя ${userId}...`);
        const data = await apiCall(`/admin/users/${userId}`, { method: 'DELETE' });
        console.log(`✅ [ADMIN-API] Пользователь ${userId} удален:`, data);
        return data;
      } catch (error) {
        console.error(`❌ [ADMIN-API] Ошибка удаления пользователя ${userId}:`, error);
        throw error;
      }
    },
  },

  qr: {
    getAllQRCodes: async () => {
      try {
        console.log('📝 [ADMIN-API] Загружаю все QR коды...');
        const data = await apiCall('/admin/qr-codes', { method: 'GET' });
        console.log('✅ [ADMIN-API] QR коды получены:', data);
        return data;
      } catch (error) {
        console.error('❌ [ADMIN-API] Ошибка получения QR кодов:', error);
        throw error;
      }
    },

    revokeQRCode: async (qrCodeId) => {
      try {
        console.log(`⛔ [ADMIN-API] Отменяю QR код ${qrCodeId}...`);
        const data = await apiCall(`/admin/qr-codes/${qrCodeId}/revoke`, {
          method: 'POST',
          body: JSON.stringify({}),
        });
        console.log(`✅ [ADMIN-API] QR код ${qrCodeId} отменен:`, data);
        return data;
      } catch (error) {
        console.error(`❌ [ADMIN-API] Ошибка отмены QR кода ${qrCodeId}:`, error);
        throw error;
      }
    },

    deleteQRCode: async (qrCodeId) => {
      try {
        console.log(`🗑️ [ADMIN-API] Удаляю QR код ${qrCodeId}...`);
        const data = await apiCall(`/admin/qr-codes/${qrCodeId}`, { method: 'DELETE' });
        console.log(`✅ [ADMIN-API] QR код ${qrCodeId} удален:`, data);
        return data;
      } catch (error) {
        console.error(`❌ [ADMIN-API] Ошибка удаления QR кода ${qrCodeId}:`, error);
        throw error;
      }
    },
  },

  transactions: {
    getAllTransactions: async () => {
      try {
        console.log('📝 [ADMIN-API] Загружаю все транзакции...');
        const data = await apiCall('/admin/transactions', { method: 'GET' });
        console.log('✅ [ADMIN-API] Транзакции получены:', data);
        return data;
      } catch (error) {
        console.error('❌ [ADMIN-API] Ошибка получения транзакций:', error);
        throw error;
      }
    },
  },

  statistics: {
    getStatistics: async () => {
      try {
        console.log('📊 [ADMIN-API] Загружаю статистику...');
        const data = await apiCall('/admin/statistics', { method: 'GET' });
        console.log('✅ [ADMIN-API] Статистика получена:', data);
        return data;
      } catch (error) {
        console.error('❌ [ADMIN-API] Ошибка получения статистики:', error);
        throw error;
      }
    },
  },

  businesses: {
    getAllBusinesses: async () => {
      try {
        console.log('📝 [ADMIN-API] Загружаю все бизнесы...');
        const data = await apiCall('/admin/businesses', { method: 'GET' });
        console.log('✅ [ADMIN-API] Бизнесы получены:', data);
        return data;
      } catch (error) {
        console.error('❌ [ADMIN-API] Ошибка получения бизнесов:', error);
        throw error;
      }
    },

    approveBusiness: async (businessId) => {
      try {
        console.log(`✅ [ADMIN-API] Одобряю бизнес ${businessId}...`);
        const data = await apiCall(`/admin/businesses/${businessId}/approve`, {
          method: 'POST',
          body: JSON.stringify({}),
        });
        console.log(`✅ [ADMIN-API] Бизнес ${businessId} одобрен:`, data);
        return data;
      } catch (error) {
        console.error(`❌ [ADMIN-API] Ошибка одобрения бизнеса ${businessId}:`, error);
        throw error;
      }
    },

    rejectBusiness: async (businessId) => {
      try {
        console.log(`❌ [ADMIN-API] Отклоняю бизнес ${businessId}...`);
        const data = await apiCall(`/admin/businesses/${businessId}/reject`, {
          method: 'POST',
          body: JSON.stringify({}),
        });
        console.log(`✅ [ADMIN-API] Бизнес ${businessId} отклонен:`, data);
        return data;
      } catch (error) {
        console.error(`❌ [ADMIN-API] Ошибка отклонения бизнеса ${businessId}:`, error);
        throw error;
      }
    },
  },

  promo: {
    listTemplates: async (filters = {}) => {
      try {
        const params = new URLSearchParams();
        if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
        if (filters.templateName) params.append('templateName', filters.templateName);
        if (filters.limit) params.append('limit', filters.limit);
        if (filters.offset) params.append('offset', filters.offset);
        const url = params.toString() ? `/admin/promo-templates?${params}` : '/admin/promo-templates';
        console.log('📝 [ADMIN-API] Загружаю промо-шаблоны...');
        const data = await apiCall(url, { method: 'GET' });
        console.log('✅ [ADMIN-API] Промо-шаблоны получены:', data);
        return data;
      } catch (error) {
        console.error('❌ [ADMIN-API] Ошибка получения промо-шаблонов:', error);
        throw error;
      }
    },

    getTemplate: async (templateId) => {
      try {
        console.log(`📝 [ADMIN-API] Получаю шаблон ${templateId}...`);
        const data = await apiCall(`/admin/promo-templates/${templateId}`, { method: 'GET' });
        console.log(`✅ [ADMIN-API] Шаблон ${templateId} получен:`, data);
        return data;
      } catch (error) {
        console.error(`❌ [ADMIN-API] Ошибка получения шаблона ${templateId}:`, error);
        throw error;
      }
    },

    createTemplate: async (templateData) => {
      try {
        console.log('✏️ [ADMIN-API] Создаю новый шаблон...');
        const data = await apiCall('/admin/promo-templates', {
          method: 'POST',
          body: JSON.stringify(templateData),
        });
        console.log('✅ [ADMIN-API] Шаблон создан:', data);
        return data;
      } catch (error) {
        console.error('❌ [ADMIN-API] Ошибка создания шаблона:', error);
        throw error;
      }
    },

    updateTemplate: async (templateId, templateData) => {
      try {
        console.log(`✏️ [ADMIN-API] Обновляю шаблон ${templateId}...`);
        const data = await apiCall(`/admin/promo-templates/${templateId}`, {
          method: 'PUT',
          body: JSON.stringify(templateData),
        });
        console.log(`✅ [ADMIN-API] Шаблон ${templateId} обновлен:`, data);
        return data;
      } catch (error) {
        console.error(`❌ [ADMIN-API] Ошибка обновления шаблона ${templateId}:`, error);
        throw error;
      }
    },

    deleteTemplate: async (templateId) => {
      try {
        console.log(`🗑️ [ADMIN-API] Удаляю шаблон ${templateId}...`);
        const data = await apiCall(`/admin/promo-templates/${templateId}`, { method: 'DELETE' });
        console.log(`✅ [ADMIN-API] Шаблон ${templateId} удален:`, data);
        return data;
      } catch (error) {
        console.error(`❌ [ADMIN-API] Ошибка удаления шаблона ${templateId}:`, error);
        throw error;
      }
    },
  },

  security: {
    getLogs: async (type = 'all', limit = 100, offset = 0) => {
      try {
        const params = new URLSearchParams({
          type,
          limit: limit.toString(),
          offset: offset.toString()
        });
        console.log('📋 [ADMIN-API] Загружаю логи безопасности...');
        const data = await apiCall(`/admin/security/logs?${params}`, { method: 'GET' });
        console.log('✅ [ADMIN-API] Логи получены:', data);
        return data;
      } catch (error) {
        console.error('❌ [ADMIN-API] Ошибка получения логов:', error);
        throw error;
      }
    },

    getSuspicious: async () => {
      try {
        console.log('🚨 [ADMIN-API] Загружаю подозрительную активность...');
        const data = await apiCall('/admin/security/suspicious', { method: 'GET' });
        console.log('✅ [ADMIN-API] Подозрительная активность получена:', data);
        return data;
      } catch (error) {
        console.error('❌ [ADMIN-API] Ошибка получения подозрительной активности:', error);
        throw error;
      }
    },

    getIPBans: async () => {
      try {
        console.log('📋 [ADMIN-API] Загружаю список заблокированных IP...');
        const data = await apiCall('/admin/security/ip-bans', { method: 'GET' });
        console.log('✅ [ADMIN-API] Список IP-запретов получен:', data);
        return data;
      } catch (error) {
        console.error('❌ [ADMIN-API] Ошибка получения IP-запретов:', error);
        throw error;
      }
    },

    banIP: async (ip, reason = '', duration = 24) => {
      try {
        console.log(`🚫 [ADMIN-API] Блокирую IP ${ip}...`);
        const data = await apiCall('/admin/security/ip-ban', {
          method: 'POST',
          body: JSON.stringify({ ip, reason, duration })
        });
        console.log(`✅ [ADMIN-API] IP ${ip} заблокирован:`, data);
        return data;
      } catch (error) {
        console.error(`❌ [ADMIN-API] Ошибка блокировки IP ${ip}:`, error);
        throw error;
      }
    },

    unbanIP: async (ip) => {
      try {
        console.log(`🔓 [ADMIN-API] Разблокирую IP ${ip}...`);
        const data = await apiCall(`/admin/security/ip-ban/${ip}`, { method: 'DELETE' });
        console.log(`✅ [ADMIN-API] IP ${ip} разблокирован:`, data);
        return data;
      } catch (error) {
        console.error(`❌ [ADMIN-API] Ошибка разблокировки IP ${ip}:`, error);
        throw error;
      }
    }
  },

  tron: {
    getWallets: async (limit = 100, offset = 0) => {
      try {
        const params = new URLSearchParams({
          limit: limit.toString(),
          offset: offset.toString()
        });
        console.log('💰 [ADMIN-API] Загружаю TRON кошельки...');
        const data = await apiCall(`/admin/tron/wallets?${params}`, { method: 'GET' });
        console.log('✅ [ADMIN-API] TRON кошельки получены:', data);
        return data;
      } catch (error) {
        console.error('❌ [ADMIN-API] Ошибка получения TRON кошельков:', error);
        throw error;
      }
    },

    getTotalBalance: async () => {
      try {
        console.log('💵 [ADMIN-API] Загружаю общий баланс TRON...');
        const data = await apiCall('/admin/tron/balance', { method: 'GET' });
        console.log('✅ [ADMIN-API] Общий баланс получен:', data);
        return data;
      } catch (error) {
        console.error('❌ [ADMIN-API] Ошибка получения общего баланса:', error);
        throw error;
      }
    },

    getTransactions: async (limit = 100, offset = 0, status = 'all') => {
      try {
        const params = new URLSearchParams({
          limit: limit.toString(),
          offset: offset.toString(),
          status
        });
        console.log('📊 [ADMIN-API] Загружаю TRON транзакции...');
        const data = await apiCall(`/admin/tron/transactions?${params}`, { method: 'GET' });
        console.log('✅ [ADMIN-API] TRON транзакции получены:', data);
        return data;
      } catch (error) {
        console.error('❌ [ADMIN-API] Ошибка получения TRON транзакций:', error);
        throw error;
      }
    },

    getNetworkStatus: async () => {
      try {
        console.log('🌐 [ADMIN-API] Загружаю статус TRON сети...');
        const data = await apiCall('/admin/tron/network-status', { method: 'GET' });
        console.log('✅ [ADMIN-API] Статус TRON сети получен:', data);
        return data;
      } catch (error) {
        console.error('❌ [ADMIN-API] Ошибка получения статуса TRON сети:', error);
        throw error;
      }
    },

    getUserWallet: async (userId) => {
      try {
        console.log(`💰 [ADMIN-API] Получаю кошелек пользователя ${userId}...`);
        const data = await apiCall(`/admin/tron/user-wallet/${userId}`, { method: 'GET' });
        console.log(`✅ [ADMIN-API] Кошелек пользователя ${userId} получен:`, data);
        return data;
      } catch (error) {
        console.error(`❌ [ADMIN-API] Ошибка получения кошелька пользователя ${userId}:`, error);
        throw error;
      }
    },

    manualTransfer: async (toAddress, amount, privateKey = null) => {
      try {
        console.log(`💸 [ADMIN-API] Выполняю ручной перевод на ${toAddress}...`);
        const data = await apiCall('/admin/tron/manual-transfer', {
          method: 'POST',
          body: JSON.stringify({ toAddress, amount, privateKey })
        });
        console.log(`✅ [ADMIN-API] Ручной перевод выполнен:`, data);
        return data;
      } catch (error) {
        console.error(`❌ [ADMIN-API] Ошибка выполнения ручного перевода:`, error);
        throw error;
      }
    },

    freezeAccount: async (userId, reason = '') => {
      try {
        console.log(`❄️ [ADMIN-API] Замораживаю кошелек пользователя ${userId}...`);
        const data = await apiCall('/admin/tron/freeze-account', {
          method: 'POST',
          body: JSON.stringify({ userId, reason })
        });
        console.log(`✅ [ADMIN-API] Кошелек пользователя ${userId} заморожен:`, data);
        return data;
      } catch (error) {
        console.error(`❌ [ADMIN-API] Ошибка замораживания кошелька:`, error);
        throw error;
      }
    },

    unfreezeAccount: async (userId) => {
      try {
        console.log(`🔥 [ADMIN-API] Размораживаю кошелек пользователя ${userId}...`);
        const data = await apiCall('/admin/tron/unfreeze-account', {
          method: 'POST',
          body: JSON.stringify({ userId })
        });
        console.log(`✅ [ADMIN-API] Кошелек пользователя ${userId} разморожен:`, data);
        return data;
      } catch (error) {
        console.error(`❌ [ADMIN-API] Ошибка размораживания кошелька:`, error);
        throw error;
      }
    }
  },

  stats: {
    getGiftQRStats: async () => {
      try {
        console.log('📊 [ADMIN-API] Загружаю расширенную статистику...');
        const data = await apiCall('/admin/stats', { method: 'GET' });
        console.log('✅ [ADMIN-API] Расширенная статистика получена:', data);
        return data;
      } catch (error) {
        console.error('❌ [ADMIN-API] Ошибка получения расширенной статистики:', error);
        throw error;
      }
    },

    getTemplateStats: async (templateId) => {
      try {
        console.log(`📊 [ADMIN-API] Получаю статистику шаблона ${templateId}...`);
        const data = await apiCall(`/admin/stats/template/${templateId}`, { method: 'GET' });
        console.log(`✅ [ADMIN-API] Статистика шаблона ${templateId} получена:`, data);
        return data;
      } catch (error) {
        console.error(`❌ [ADMIN-API] Ошибка получения статистики шаблона ${templateId}:`, error);
        throw error;
      }
    },

    getActivationStats: async (period = 'week') => {
      try {
        console.log(`📊 [ADMIN-API] Получаю статистику активаций за ${period}...`);
        const data = await apiCall(`/admin/stats/activations?period=${period}`, { method: 'GET' });
        console.log(`✅ [ADMIN-API] Статистика активаций за ${period} получена:`, data);
        return data;
      } catch (error) {
        console.error(`❌ [ADMIN-API] Ошибка получения статистики активаций:`, error);
        throw error;
      }
    },

    getFullReport: async (period = 'week') => {
      try {
        console.log(`📊 [ADMIN-API] Получаю полный отчет за ${period}...`);
        const data = await apiCall(`/admin/stats/report?period=${period}`, { method: 'GET' });
        console.log(`✅ [ADMIN-API] Полный отчет за ${period} получен:`, data);
        return data;
      } catch (error) {
        console.error(`❌ [ADMIN-API] Ошибка получения полного отчета:`, error);
        throw error;
      }
    },

    getChartData: async (period = 'week') => {
      try {
        console.log(`📊 [ADMIN-API] Получаю данные для графиков за ${period}...`);
        const data = await apiCall(`/admin/stats/charts?period=${period}`, { method: 'GET' });
        console.log(`✅ [ADMIN-API] Данные для графиков за ${period} получены:`, data);
        return data;
      } catch (error) {
        console.error(`❌ [ADMIN-API] Ошибка получения данных для графиков:`, error);
        throw error;
      }
    },
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// SIMPLE HTTP METHODS (для совместимости)
// ═══════════════════════════════════════════════════════════════════════════
const api = {
  get: async (endpoint) => {
    return apiCall(endpoint, { method: 'GET' });
  },
  post: async (endpoint, data) => {
    return apiCall(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },
  put: async (endpoint, data) => {
    return apiCall(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },
  patch: async (endpoint, data) => {
    return apiCall(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  },
  delete: async (endpoint) => {
    return apiCall(endpoint, { method: 'DELETE' });
  }
};
export { api };

// ═══════════════════════════════════════════════════════════════════════════
// EXPORT
// ═══════════════════════════════════════════════════════════════════════════

export default {
  authAPI,
  profileAPI,
  balanceAPI,
  transactionsAPI,
  qrAPI,
  adminAPI,
  apiCall,
};

